import { Container, Graphics, Text } from 'pixi.js';

import { WORLD_TILE_HEIGHT, WORLD_TILE_WIDTH } from '../../game/config';
import type { GameWorldSnapshot, WorldEntity } from '../world/types';

interface ActivityView {
  readonly root: Container;
  readonly graphics: Graphics;
  readonly label: Text;
}

export class BuildingActivityRenderer {
  private readonly root = new Container();
  private readonly views = new Map<string, ActivityView>();

  constructor(layer: Container) {
    this.root.label = 'Gebäudeaktivitäten';
    this.root.sortableChildren = true;
    layer.addChild(this.root);
  }

  render(snapshot: GameWorldSnapshot, elapsedMs = performance.now()): void {
    const activeIds = new Set<string>();
    for (const building of snapshot.buildings) {
      if (building.activity !== 'work' || !building.activeWorkers) continue;
      activeIds.add(building.id);
      const view = this.viewFor(building.id);
      this.draw(view, building, elapsedMs / 1000);
    }
    for (const [id, view] of this.views) {
      view.root.visible = activeIds.has(id);
    }
  }

  private viewFor(id: string): ActivityView {
    const existing = this.views.get(id);
    if (existing) {
      existing.root.visible = true;
      return existing;
    }
    const root = new Container();
    const graphics = new Graphics();
    const label = new Text({
      text: '',
      style: {
        fill: '#fff0b5',
        fontFamily: 'Georgia, serif',
        fontSize: 14,
        fontWeight: 'bold',
        stroke: { color: '#241208', width: 4 },
        dropShadow: { color: '#000000', alpha: 0.55, blur: 2, distance: 1 },
      },
    });
    label.anchor.set(0.5);
    root.addChild(graphics, label);
    this.root.addChild(root);
    const view = { root, graphics, label };
    this.views.set(id, view);
    return view;
  }

  private draw(view: ActivityView, building: WorldEntity, time: number): void {
    const x = building.x * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2;
    const y = building.y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT;
    const workers = building.activeWorkers ?? 1;
    const pulse = (Math.sin(time * 4.2 + building.x) + 1) / 2;
    view.root.position.set(x, y);
    view.root.zIndex = Math.floor(y + 80);
    view.graphics.clear();

    // Warm doorway light and tiny tool sparks show that work is happening
    // without revealing the character through the closed building.
    view.graphics
      .roundRect(-12, -24, 24, 18, 4)
      .fill({ color: 0xffbd4a, alpha: 0.18 + pulse * 0.12 });
    for (let index = 0; index < 5; index += 1) {
      const phase = (time * 1.7 + index * 0.21) % 1;
      const sparkX = Math.sin(index * 4.7 + time * 2.1) * 16;
      const sparkY = -24 - phase * 34;
      view.graphics
        .rect(Math.round(sparkX), Math.round(sparkY), 3, 3)
        .fill({ color: index % 2 ? 0xffd970 : 0xff8a32, alpha: 0.72 - phase * 0.5 });
    }

    // Chimney smoke remains soft and voxel-compatible.
    for (let index = 0; index < 3; index += 1) {
      const phase = (time * 0.22 + index * 0.31) % 1;
      const smokeX = 18 + Math.sin(time * 0.8 + index) * 8 + phase * 10;
      const smokeY = -82 - phase * 54;
      view.graphics
        .circle(smokeX, smokeY, 5 + phase * 7)
        .fill({ color: 0xe9e3d1, alpha: 0.24 * (1 - phase) });
    }

    view.graphics
      .roundRect(-58, -116, 116, 24, 7)
      .fill({ color: 0x24170d, alpha: 0.86 })
      .stroke({ color: 0xd7a746, width: 2, alpha: 0.92 });
    view.label.text = `IN ARBEIT · ${workers}`;
    view.label.position.set(0, -104);
  }
}
